/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C1f §6] WHEN THE ARRANGEMENT BUILDER MAY WRITE, AND WHY IT IS SAFE TO AGAIN.
 *
 *  ⚠️ THIS REVERSES [P3b.3], AND IT NAMES THE MECHANISM THAT KILLED THE FIRST AUTOSAVE.
 *
 *  P3b.3 removed a debounce that wrote 800 ms after a KEYSTROKE. The write invalidated
 *  `queryKeys.arrangements.all`, which is a PREFIX of `detail(id)`, so every save REFETCHED
 *  the record being edited — and the seeding effect wrote the server's copy back over the
 *  form. That copy is not what is on screen: `body()` sends `submittableLines`, which strips
 *  a cell at zero and a source with no division chosen. A `10` typed down to `1` snapped
 *  back, a vehicle `0` typed to `1` snapped back, and a freshly added source row vanished a
 *  moment after appearing.
 *
 *  ⚠️ THE NEW ONE CANNOT REPRODUCE IT, AND THE REASON IS NOT CARE — BOTH HALVES ARE GONE.
 *
 *    1. NO REFETCH EXISTS TO RACE. The response is written with `setQueryData(detail(id))`
 *       and the only invalidations are `lists()` — not a prefix of `detail` — and
 *       `publishPreview(id)`, which is a strict DESCENDANT, so refetching it cannot refetch
 *       its parent. `queryKeys.arrangements.all` does not appear on this page at all, and
 *       `saveKeyIsolation.test.ts` reads the source and fails if it ever does again.
 *    2. NO KEYSTROKE TRIGGERS A WRITE. The triggers are BLUR, STEP CHANGE and UNMOUNT. A
 *       field being typed into has not been left, so nothing is ever in flight while the
 *       operator is mid-value — which is the situation the old timer existed to create.
 *
 *  ⚠️ AND THE `seededFor` GUARD IS WIDENED RATHER THAN RELAXED. It is now set in the save's
 *  own `onSuccess` as well as in the seeding effect, because the CREATE transition — save,
 *  navigate to `/edit`, `existing` mounts — was never covered by it, and that path re-seeded
 *  the form from the server's stripped copy once on every new order.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * ⚠️ FOUR, AND NOT ONE OF THEM IS A TIMER. Stated as a tuple so `saveKeyIsolation.test.ts`
 * can read the page's source and prove every one is wired — and so that adding a fifth is a
 * deliberate edit rather than a line somebody slips into an effect.
 */
export const SAVE_TRIGGERS = ['blur', 'step', 'unmount', 'save-button', 'attach'] as const;
export type SaveTrigger = (typeof SAVE_TRIGGERS)[number];

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [AUTO-SAVE] WITH `ENABLE_AUTO_SAVE=false` THE SAVE BUTTON IS THE ONLY TRIGGER THAT WRITES.
 *
 *  Blur, step change and unmount are autosave by definition. `attach` is too, though the operator
 *  fires it on purpose: on an order not yet saved it CREATES the draft from whatever the form holds,
 *  which is a save of the whole form that nobody pressed Save for — so with autosave off the chosen
 *  PDF waits in the queue for that press, and the Files section says so. On a saved order
 *  `AttachmentList` uploads the file itself and writes nothing of the form, so that path is the same
 *  in both modes.
 *
 *  ⚠️ A NARROWING OF `flush`, NEVER A SECOND WRITER. Every trigger still goes through the one
 *  function; this only decides whether the one it was handed may reach `save.mutate`.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
export const MANUAL_SAVE_TRIGGERS = ['save-button'] as const satisfies readonly SaveTrigger[];

export const triggerMaySave = (trigger: SaveTrigger, autoSave: boolean): boolean =>
  autoSave || (MANUAL_SAVE_TRIGGERS as readonly SaveTrigger[]).includes(trigger);

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C3a §7] WHERE THE SAVE GOT TO — AND IT LIVES HERE BECAUSE `publishControl.ts` READS IT.
 *
 *  §7d: whatever gates Publish must read the SAME value the save path writes. That makes this a
 *  shared vocabulary rather than a page-local one, and `publishControl.ts` importing it from a
 *  page's `index.tsx` would be a module boundary pointing the wrong way.
 *
 *  ⚠️ AND `'publish-attempt'` BECAME `'save-button'` IN THE SAME EDIT. That trigger was wired at
 *  exactly one site — inside the Publish button's `onClick`, for the arm where Publish saved
 *  instead of publishing. §7a removes that arm: a save that has not happened now renders a SAVE
 *  control, so the trigger is named after the thing that fires it, as its three siblings are.
 *  `saveKeyIsolation.test.ts` greps the page for every member of this tuple, so the rename is
 *  proved wired by a test that needed no edit.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
export const SAVE_STATES = ['idle', 'unsaved', 'saving', 'saved', 'failed', 'needs-date'] as const;
export type SaveState = (typeof SAVE_STATES)[number];

/**
 * ⚠️ [C3a §7a] THE STATES IN WHICH A SAVE IS OWED — the one place that set is written down.
 *
 * `saving` is in it deliberately: a write in flight has not landed, and offering Publish beside
 * a spinner invites the click that [C2a]'s data loss came out of. `idle` is NOT: nothing has
 * been touched since the record loaded, so there is nothing to save and nothing to warn about.
 */
export const SAVE_PENDING_STATES = ['unsaved', 'saving', 'failed', 'needs-date'] as const;
export const saveIsPending = (state: SaveState): boolean =>
  (SAVE_PENDING_STATES as readonly SaveState[]).includes(state);

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [AUTO-SAVE] WHAT THE LEAVE PROMPT SAYS — ONE SENTENCE PER REASON THE FORM IS NOT SAVED.
 *
 *  With autosave ON the guard stops an in-app exit only for work autosave cannot save: a missing
 *  date, a failed write, a PDF still queued. With it OFF it stops one for EVERY unsaved change,
 *  which the two older sentences cannot describe — "the last save did not go through" is false of
 *  an edit nobody has tried to save yet.
 *
 *  ⚠️ AND THE THIRD SENTENCE ALSO CORRECTS AUTOSAVE ON. A queued PDF on a form whose save is in
 *  flight or has landed used to be told "The last save did not go through", which was never true
 *  of it; it is told it has unsaved changes now. The two older sentences keep their states.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
export const GUARD_REASONS = ['needs-date', 'failed', 'unsaved'] as const;
export type GuardReason = (typeof GUARD_REASONS)[number];

export const guardReasonOf = (state: SaveState): GuardReason =>
  state === 'needs-date' ? 'needs-date' : state === 'failed' ? 'failed' : 'unsaved';

export type Arming =
  'armed' | 'read-only' | 'not-hydrated' | 'nothing-to-save' | 'no-content' | 'no-date' | 'in-flight';

export type ArmingInput = {
  readOnly: boolean;
  /** [P3a.1] A form opened on an existing record is not ready to write until it is seeded. */
  hydrated: boolean;
  dirty: boolean;
  /**
   * ⚠️ [UX1.2] HAS THE OPERATOR SUPPLIED ANYTHING? — `hasMeaningfulContent` below.
   *
   * `dirty` says the form CHANGED; this says the change was WORTH KEEPING. Adding an empty
   * duty point changes the form and supplies nothing, and that is the whole of the defect.
   */
  hasContent: boolean;
  /** The order date, as the form holds it. `false` means "not set". */
  hasPaDate: boolean;
  inFlight: boolean;
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [UX1.2] WHAT THE **OPERATOR** SUPPLIED — WHICH IS NOT "IS ANY FIELD SET".
 *
 *  Autosave armed on `dirty` alone, so clicking "Add duty point" and walking away created a
 *  dated, empty order: the row existing IS a change, and `onValuesChange` cannot tell a
 *  structural one from a typed one. Somebody then has to tidy that list.
 *
 *  ⚠️ AND A DEFAULT IS NOT CONTENT. A fresh card is NOT blank — `newDutyPoint` gives it a
 *  minted id, ONE BLANK PLACE ROW, `timingKind: 'NO_FIXED_TIME'`, a `DEFAULT_SHIFT()`,
 *  `recurrenceKind: 'ONE_DAY'`, a `startDate` inherited from the order date, and — since
 *  [C1f] — THE DEFAULT POLICY, which `seededLines` then turns into a requirement line per
 *  column, all at zero. A predicate asking "is any field set" is satisfied by every one of
 *  those and nothing would have changed.
 *
 *  So each test below is written against what the SEEDER produces, not against emptiness:
 *  a place must be non-blank, a requirement line must ask for a NON-ZERO count, and the
 *  policy, the schedule and the id are not consulted at all.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * ⚠️ THE TAB-1 FIELDS, CLASSIFIED — AND `autosave.test.ts` READS `Sections.tsx` TO PROVE THE
 * LIST IS COMPLETE.
 *
 * This file's own header used to refuse a field list, on the grounds that it "has to be edited
 * every time tab 1 gains a field and forgetting is SILENT". That objection is RIGHT and it is
 * why the guard exists rather than why the list does not: a new `Form.Item` in `BasicsSection`
 * that appears in neither tuple fails a test instead of quietly never arming a save.
 *
 * ⚠️ `paDate` IS THE ONLY DEFAULTED ONE, and it is the reason this cannot be "any tab-1 field
 * is set": it PRE-FILLS to today, so a gate including it is satisfied the instant the page
 * mounts — which is the bug in its original form.
 */
export const PA_CONTENT_FIELDS = [
  'paNumber',
  'title',
  'description',
  'estimatedCrowdSize',
  'associatedOrgUnitIds',
  'instructions',
  'supportingArrangements',
] as const;

/** Supplied by the form itself, so it can never mean the operator typed something. */
export const PA_DEFAULTED_FIELDS = ['paDate'] as const;

type PlaceLike = { placeId?: string | null; name?: string };

/** Only the parts a content test reads. Structural keys are deliberately absent. */
export type DutyPointContent = {
  name?: string;
  places?: readonly (PlaceLike | undefined)[];
  reportingPlace?: PlaceLike;
  reportingTime?: string;
  standDownTime?: string;
  zone?: string;
  sector?: string;
  requirementLines?: readonly { requiredCount?: number; allocations?: readonly { count?: number }[] }[];
  commandEntries?: readonly unknown[];
  instructions?: readonly unknown[];
  supportingArrangements?: readonly unknown[];
};

export type ContentInput = {
  pa: Readonly<Record<string, unknown>>;
  dutyPoints: readonly DutyPointContent[];
  /**
   * ⚠️ [C3a §3] A PDF THE OPERATOR CHOSE, WAITING FOR A RECORD TO HANG OFF.
   *
   * OPTIONAL, so the eight existing `hasMeaningfulContent(...)` call sites in `autosave.test.ts`
   * compile untouched — [P6d]'s test: the field's PRESENCE is the signal a different path ran,
   * and a caller with no queue has nothing to render for it.
   *
   * ⚠️ AND IT IS UNAMBIGUOUSLY CONTENT. [UX1.2]'s question is what the OPERATOR supplied, and
   * nothing about a chosen file is a default, a seed or a structural row — which is exactly what
   * the three tests beside this one exist to exclude.
   */
  pendingAttachments?: number;
};

/** A trimmed string, a non-empty array, or any other present value. */
const filled = (value: unknown): boolean => {
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return value !== undefined && value !== null;
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C5c §8e] A ROW IS CONTENT IFF THE WIRE KEEPS IT — and these two ARE that rule, exported.
 *
 *  `filled` on an array asks a question about STRUCTURE: "Add line" appends `{ type: 'LINE',
 *  text: '' }` and "Add person" `{ personName: '' }`, so an operator who opened a panel, pressed
 *  the button and walked away armed a CREATE of a dated, empty draft — a large part of what the
 *  thirty-day sweep would otherwise be sweeping up. The save already dropped both rows (the wire's
 *  `text` and `personName` are `.min(1)`), so the arming check and the body disagreed about the
 *  same row.
 *
 *  ⚠️ ONE PREDICATE, EVERYWHERE THE QUESTION IS ASKED: this file's two content checks, the PA
 *  builder's body (`index.tsx`, which also fixes the order-level "Add person" 422), the card body
 *  (`formValues.ts`), the round-the-clock body and the checklist's instruction counts. A second
 *  spelling of "is this line real" is how the checklist came to tick for a line the save dropped.
 *
 *  ⚠️ TOTAL OVER `unknown`, because the form store is rc-field-form's untyped value and a
 *  half-registered row can be `undefined`. A person with RESPONSIBILITIES and no name is not
 *  kept either: the UI offers no responsibilities input, and the body drops a nameless row.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const instructionIsReal = (row: unknown): boolean =>
  typeof row === 'object' && row !== null && typeof (row as { text?: unknown }).text === 'string'
    ? (row as { text: string }).text.trim().length > 0
    : false;

export const supportingPersonIsReal = (row: unknown): boolean =>
  typeof row === 'object' && row !== null && typeof (row as { personName?: unknown }).personName === 'string'
    ? (row as { personName: string }).personName.trim().length > 0
    : false;

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C6a · C5c §8e] A ROW IS CONTENT IFF THE WIRE KEEPS IT — and these two are that rule for command.
 *
 *  `commandAskIsReal` is what `toDutyPointInput` strips by: a position with no division is a row
 *  somebody has not finished, and `commandAskInputSchema` requires the division, so sending it
 *  would 422 every autosave (divergence #30's shape). `commandEntryHasContent` is what the
 *  autosave gate arms on, and it asks the SAME predicate of the asks — a gate may not test a row
 *  the body strips, or an empty toggle arms a create for an untouched order.
 *
 *  ⚠️ A ROLE ENTRY WITH NOTHING IN IT IS NOT CONTENT. `setOfficers` leaves `{ roleKey,
 *  officerIds: [] }` behind when the last officer is cleared, and choosing "Ask a division"
 *  writes nothing at all until Confirm — so the only things an operator can have SUPPLIED are a
 *  one-off label, a named officer and a real ask.
 *
 *  ⚠️ TOTAL OVER `unknown`, because the form store is rc-field-form's untyped value —
 *  `instructionIsReal`'s rule.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const commandAskIsReal = (ask: unknown): boolean =>
  typeof ask === 'object' &&
  ask !== null &&
  typeof (ask as { divisionOrgUnitId?: unknown }).divisionOrgUnitId === 'string' &&
  (ask as { divisionOrgUnitId: string }).divisionOrgUnitId.trim().length > 0;

export const commandEntryHasContent = (entry: unknown): boolean => {
  if (typeof entry !== 'object' || entry === null) return false;
  const row = entry as { adHocLabel?: unknown; officerIds?: unknown; asks?: unknown };
  return (
    (typeof row.adHocLabel === 'string' && row.adHocLabel.trim().length > 0) ||
    (Array.isArray(row.officerIds) && row.officerIds[0] !== undefined) ||
    (Array.isArray(row.asks) && row.asks.some(commandAskIsReal))
  );
};

const someRow =
  (isReal: (row: unknown) => boolean) =>
  (value: unknown): boolean =>
    Array.isArray(value) && value.some(isReal);

/**
 * ⚠️ [C5c §8e] EVERY TAB-1 FIELD'S CONTENT TEST, AS A `Record` OVER `PA_CONTENT_FIELDS`, so a field
 * added to the tuple and left unclassified here fails to COMPILE rather than falling back to
 * `filled` — which is exactly how the two list fields came to be read as structure.
 */
const PA_FIELD_HAS_CONTENT: Record<(typeof PA_CONTENT_FIELDS)[number], (value: unknown) => boolean> = {
  paNumber: filled,
  title: filled,
  description: filled,
  estimatedCrowdSize: filled,
  associatedOrgUnitIds: filled,
  instructions: someRow(instructionIsReal),
  supportingArrangements: someRow(supportingPersonIsReal),
};

/**
 * ⚠️ A BLANK ROW IS NOT A PLACE. `newDutyPoint` seeds `[{ placeId: null, name: '' }]` so the
 * card always renders one input — [D4.5 §2b]'s "the read side puts back what the write side
 * strips" — so counting rows would arm on every fresh card.
 */
const placeIsReal = (place: PlaceLike | undefined): boolean =>
  place !== undefined && (filled(place.placeId) || filled(place.name));

/**
 * ⚠️ SEEDED LINES ARE NOT A REQUIREMENT. `seededLines` writes one line per usable column with
 * `requiredCount: 0` and no allocations, so the test is a NON-ZERO count — the number the
 * operator typed — and never the presence of a line.
 */
const asksForForce = (point: DutyPointContent): boolean =>
  (point.requirementLines ?? []).some(
    (line) => (line.requiredCount ?? 0) > 0 || (line.allocations ?? []).some((row) => (row.count ?? 0) > 0),
  );

export const dutyPointHasContent = (point: DutyPointContent): boolean =>
  (point.places ?? []).some(placeIsReal) ||
  placeIsReal(point.reportingPlace) ||
  filled(point.reportingTime) ||
  filled(point.standDownTime) ||
  /* [C4a §4] A typed zone is something the operator supplied, so it arms a save — [C5c §4] and a sector. */
  filled(point.zone) ||
  filled(point.sector) ||
  filled(point.name) ||
  asksForForce(point) ||
  /*
   * ⚠️ [C6a] A COMMAND ROW IS CONTENT ONLY WHEN IT HOLDS SOMETHING — a one-off label, a named
   * officer or a REAL ask — through the SAME predicate `toDutyPointInput` strips by
   * (`commandEntryHasContent`, [C5c §8e]). It was `filled(commandEntries)`, a length test, under
   * which a bare `{ roleKey }` armed a save: harmless while such a row could only be the residue of
   * clearing an officer, and the [C5c §8e] empty-draft cause once a half-built ask could sit there.
   *
   * ⚠️ THE COST, STATED: A SAVED DRAFT WHOSE ONLY CONTENT WAS ONE OFFICER OR ONE ASK IS NOT SAVED
   * WHEN THAT IS CLEARED. The residue `{ roleKey, officerIds: [] }` counted under the old length test,
   * so the clear PATCHed; now `armingOf` answers `no-content`, `flush` resolves it to
   * `markClean('idle')`, and the cleared officer or ask is back on reload. That is [C3a §7b]'s rule
   * for EVERY field — clearing an order back to nothing is resolved to idle, never written — which
   * command rows now obey rather than escape. Accepted, not fixed: saving a clear only on a SAVED
   * record would reverse that rule for all of tab 1 and tab 2 at once, which is not this pass's call.
   */
  (point.commandEntries ?? []).some(commandEntryHasContent) ||
  /* ⚠️ [C5c §8e] A blank line or a blank person is a ROW, not content — see `instructionIsReal`. */
  (point.instructions ?? []).some(instructionIsReal) ||
  (point.supportingArrangements ?? []).some(supportingPersonIsReal);

/**
 * ⚠️ EITHER SIDE IS ENOUGH, WHICH IS THE HALF A TAB-1 FIELD LIST ALONE WOULD GET WRONG.
 *
 * An operator who skips to tab 2 and names a place has entered something worth keeping, and a
 * gate that only read tab 1 would refuse to keep it. That is the objection this file's header
 * already made against a field list, and it survives: the list is one DISJUNCT, not the rule.
 */
export const hasMeaningfulContent = (input: ContentInput): boolean =>
  PA_CONTENT_FIELDS.some((field) => PA_FIELD_HAS_CONTENT[field](input.pa[field])) ||
  input.dutyPoints.some(dutyPointHasContent) ||
  (input.pendingAttachments ?? 0) > 0;

/**
 * ⚠️ [C1f §6] "TAB ONE HOLDS SOMETHING" IS TWO CONJUNCTS, AND NEITHER IS A LIST OF FIELDS.
 *
 *   • `hasPaDate` is the WIRE's minimum. `createArrangementSchema` requires `paDate` and
 *     nothing else, so a POST without it is a 422 rather than a draft — this is exactly what
 *     the old `Save draft` button refused on, moved to where the decision is now taken.
 *   • `dirty` is the OPERATOR's minimum. `paDate` PRE-FILLS to today, so a gate on the date
 *     alone is satisfied the instant the page mounts and would create a dated, empty order for
 *     anybody who opened the form and walked away. `dirty` is set only by `onValuesChange`,
 *     which `hydrated` already keeps a seeding write out of.
 *
 * ⚠️ IT IS DELIBERATELY NOT `paDate | paOrderNo | title | description | crowdSize`. That list
 * has to be edited every time tab 1 gains a field and forgetting is SILENT — the new field
 * marks the form dirty, the indicator reads "Not saved", and nothing ever writes. It is also
 * wrong about scope: an operator who skips to tab 2 and adds a duty point has entered
 * something worth keeping, and a tab-1 field list would refuse to keep it.
 *
 * Ordered and exhaustive, so the reason a write did not happen is always exactly one thing.
 */
export const armingOf = (input: ArmingInput): Arming => {
  if (input.readOnly) return 'read-only';
  if (!input.hydrated) return 'not-hydrated';
  if (!input.dirty) return 'nothing-to-save';
  /*
   * ⚠️ [UX1.2] BEFORE `no-date`, AND THE ORDER IS THE DIFFERENCE BETWEEN SILENCE AND A NAG.
   *
   * `no-date` is reported to the operator — the indicator reads "needs a date", because a form
   * with content and no date is a save that WOULD have happened. `no-content` is not a refusal
   * and must not read as one: somebody who has opened the builder and typed nothing has made
   * no mistake. Checking content first is what keeps the indicator quiet for them.
   */
  if (!input.hasContent) return 'no-content';
  if (!input.hasPaDate) return 'no-date';
  if (input.inFlight) return 'in-flight';
  return 'armed';
};

export const isArmed = (input: ArmingInput): boolean => armingOf(input) === 'armed';
